"use client";

import type { SessionCountdown } from "@/hooks/useSessionCountdown";
import { LivePulse } from "./Countdown";
import { SESSION_TITLE } from "@/lib/masterclass";

// ============================================================================
// L'image du hero : un créneau bloqué dans un agenda.
//   Format recommandé par le doc Ads (« la capture de calendrier — natif,
//   reconnaissable, zéro effort de lecture »). Construit en HTML plutôt qu'en
//   asset : il reste net à toute densité, se recolore avec le thème et affiche
//   la vraie date de la prochaine session.
//   Une seule image pour le bloc — deux s'annulent.
// ============================================================================

// Deux lignes de contexte suffisent à faire lire « agenda » : chaque ligne en
// plus repousse le CTA sous la ligne de flottaison sur un écran de 667 px.
const AROUND: { at: string; label: string }[] = [
  { at: "11:00", label: "Point équipe" },
];

export function CalendarSlot({
  c,
  className,
}: {
  c: SessionCountdown;
  className?: string;
}) {
  return (
    <div
      className={
        "mx-auto w-full max-w-sm overflow-hidden rounded-2xl border border-ink/10 bg-white text-left shadow-xl shadow-ink/5 " +
        (className ?? "")
      }
    >
      {/* En-tête : la date réelle de la prochaine session */}
      <div className="flex items-center justify-between border-b border-ink/10 bg-cream-dark/50 px-4 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink/50">
          {c.ready ? c.dateLabel : " "}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-ink/35">
          Agenda
        </span>
      </div>

      <div className="px-4 py-2">
        {AROUND.map((row) => (
          <Row key={row.at} at={row.at}>
            {row.label && (
              <div className="h-8 rounded-md border-l-2 border-ink/15 bg-ink/[0.04] px-2.5 py-1.5 text-xs text-ink/40">
                {row.label}
              </div>
            )}
          </Row>
        ))}

        {/* Le créneau qui compte */}
        <Row at="12:30" strong>
          <div className="rounded-md border-l-[3px] border-brand bg-brand/10 px-2.5 py-2">
            <p className="flex items-center gap-1.5 text-xs font-bold text-brand">
              <LivePulse />
              {SESSION_TITLE.split(" — ")[0]}
            </p>
            <p className="mt-0.5 text-[11px] text-ink/55">
              12h30 – 13h30 · 1 h · en direct
            </p>
          </div>
        </Row>

        <Row at="13:30">
          <div className="h-7 rounded-md border-l-2 border-ink/15 bg-ink/[0.04] px-2.5 py-1 text-xs text-ink/40">
            Déjeuner
          </div>
        </Row>
      </div>
    </div>
  );
}

function Row({
  at,
  strong = false,
  children,
}: {
  at: string;
  strong?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 border-t border-ink/[0.06] py-2 first:border-t-0">
      <span
        className={
          "w-11 shrink-0 pt-1.5 text-[11px] tabular-nums " +
          (strong ? "font-bold text-ink/70" : "text-ink/35")
        }
      >
        {at}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
